interface StrokePoint {
  x: number;
  y: number;
}

function generateUuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function formatPdfDate(): string {
  const now = new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');

  const offsetMin = now.getTimezoneOffset();
  const sign = offsetMin <= 0 ? '+' : '-';
  const absOffset = Math.abs(offsetMin);
  const tzH = pad(Math.floor(absOffset / 60));
  const tzM = pad(absOffset % 60);

  return (
    `D:${now.getFullYear()}` +
    `${pad(now.getMonth() + 1)}` +
    `${pad(now.getDate())}` +
    `${pad(now.getHours())}` +
    `${pad(now.getMinutes())}` +
    `${pad(now.getSeconds())}` +
    `${sign}${tzH}'${tzM}'`
  );
}

function escapeXml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Scale raw canvas strokes into PDF coordinate space, fitting inside the
 * target signature widget rect.
 *
 * The targetRect uses PDFTron's coordinate system (top-left origin, Y down).
 * The output must be in XFDF/PDF space (bottom-left origin, Y up).
 * pageHeight is needed for the conversion.
 */
function scaleStrokesToRect(
  strokes: StrokePoint[][],
  canvasWidth: number,
  canvasHeight: number,
  targetRect: { x: number; y: number; width: number; height: number },
  pageHeight: number
): { scaledStrokes: StrokePoint[][]; rect: [number, number, number, number] } {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const stroke of strokes) {
    for (const p of stroke) {
      if (p.x < minX) {
        minX = p.x;
      }
      if (p.y < minY) {
        minY = p.y;
      }
      if (p.x > maxX) {
        maxX = p.x;
      }
      if (p.y > maxY) {
        maxY = p.y;
      }
    }
  }

  const strokeW = maxX - minX || 1;
  const strokeH = maxY - minY || 1;

  const hScale = targetRect.width / strokeW;
  const vScale = targetRect.height / strokeH;
  const scale = Math.min(hScale, vScale) * 0.85;

  const scaledW = strokeW * scale;
  const scaledH = strokeH * scale;

  const rectX1 = targetRect.x;
  const rectY1 = pageHeight - targetRect.y - targetRect.height;
  const rectX2 = targetRect.x + targetRect.width;
  const rectY2 = pageHeight - targetRect.y;

  const offsetX = rectX1 + (targetRect.width - scaledW) / 2;
  const offsetY = rectY1 + (targetRect.height - scaledH) / 2;

  const scaledStrokes = strokes.map((stroke) =>
    stroke.map((p) => ({
      x: offsetX + (p.x - minX) * scale,
      y: offsetY + scaledH - (p.y - minY) * scale,
    }))
  );

  return { scaledStrokes, rect: [rectX1, rectY1, rectX2, rectY2] };
}

export function buildInkXfdf(
  strokes: StrokePoint[][],
  canvasWidth: number,
  canvasHeight: number,
  targetRect: { x: number; y: number; width: number; height: number },
  page: number,
  pageHeight: number,
  userName: string,
  customData?: Record<string, unknown>
): string {
  const { scaledStrokes, rect } = scaleStrokesToRect(
    strokes,
    canvasWidth,
    canvasHeight,
    targetRect,
    pageHeight
  );

  const [x1, y1, x2, y2] = rect;
  const date = formatPdfDate();
  const uuid = generateUuid();

  const customDataStr = customData ? JSON.stringify(customData) : '';
  const trnBytes = customDataStr
    ? `{"trn-annot-maintain-aspect-ratio":"true","custom-data":"${customDataStr.replace(
        /"/g,
        '\\"'
      )}"}`
    : '{"trn-annot-maintain-aspect-ratio":"true"}';

  const gestures = scaledStrokes
    .map((stroke) => {
      const points = stroke.map((p) => `${p.x},${p.y}`).join(';');
      return `<gesture>${points}</gesture>`;
    })
    .join('');

  return (
    `<?xml version="1.0" encoding="UTF-8" ?>` +
    `<xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve">` +
    `<annots>` +
    `<ink page="${page}" rect="${x1.toFixed(3)},${y1.toFixed(3)},${x2.toFixed(
      3
    )},${y2.toFixed(3)}" ` +
    `color="#000000" flags="print" name="${uuid}" ` +
    `title="${escapeXml(userName)}" subject="Signature" ` +
    `date="${date}" creationdate="${date}">` +
    `<trn-custom-data bytes="${escapeXml(trnBytes)}"/>` +
    `<inklist>${gestures}</inklist>` +
    `</ink>` +
    `</annots></xfdf>`
  );
}

export function buildFreetextXfdf(
  text: string,
  fontFamily: string,
  targetRect: { x: number; y: number; width: number; height: number },
  page: number,
  pageHeight: number,
  userName: string,
  customData?: Record<string, unknown>
): string {
  const date = formatPdfDate();
  const uuid = generateUuid();

  const { x, y, width, height } = targetRect;
  const x1 = x;
  const y1 = pageHeight - y - height;
  const x2 = x + width;
  const y2 = pageHeight - y;

  const style = `font-family: ${fontFamily};`;

  const customDataStr = customData ? JSON.stringify(customData) : '';
  const trnBytes = customDataStr
    ? `{"custom-data":"${customDataStr.replace(/"/g, '\\"')}"}`
    : '';
  const trnTag = trnBytes
    ? `<trn-custom-data bytes="${escapeXml(trnBytes)}"/>`
    : '';

  return (
    `<?xml version="1.0" encoding="UTF-8" ?>` +
    `<xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve">` +
    `<annots>` +
    `<freetext page="${page}" rect="${x1.toFixed(3)},${y1.toFixed(
      3
    )},${x2.toFixed(3)},${y2.toFixed(3)}" ` +
    `color="#000000" flags="print" name="${uuid}" ` +
    `title="${escapeXml(userName)}" subject="Signature" ` +
    `style="${escapeXml(style)}" ` +
    `date="${date}" creationdate="${date}">` +
    trnTag +
    `<contents>${escapeXml(text)}</contents>` +
    `<defaultstyle>${escapeXml(style)}</defaultstyle>` +
    `</freetext>` +
    `</annots></xfdf>`
  );
}

/**
 * Build a "standalone" ink XFDF for saving as a reusable signature (not bound
 * to a particular widget rect). Uses absolute canvas coordinates in a rect
 * derived from the stroke bounding box.
 */
export function buildStandaloneInkXfdf(
  strokes: StrokePoint[][],
  userName: string
): string {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const stroke of strokes) {
    for (const p of stroke) {
      if (p.x < minX) {
        minX = p.x;
      }
      if (p.y < minY) {
        minY = p.y;
      }
      if (p.x > maxX) {
        maxX = p.x;
      }
      if (p.y > maxY) {
        maxY = p.y;
      }
    }
  }

  const padding = 5;
  const x1 = minX - padding;
  const y1 = minY - padding;
  const x2 = maxX + padding;
  const y2 = maxY + padding;
  const height = y2 - y1;

  const flippedStrokes = strokes.map((stroke) =>
    stroke.map((p) => ({
      x: p.x,
      y: y1 + height - (p.y - y1),
    }))
  );

  const date = formatPdfDate();
  const uuid = generateUuid();

  const gestures = flippedStrokes
    .map((stroke) => {
      const points = stroke.map((p) => `${p.x},${p.y}`).join(';');
      return `<gesture>${points}</gesture>`;
    })
    .join('');

  return (
    `<?xml version="1.0" encoding="UTF-8" ?>` +
    `<xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve">` +
    `<annots>` +
    `<ink page="0" rect="${x1.toFixed(3)},${y1.toFixed(3)},${x2.toFixed(
      3
    )},${y2.toFixed(3)}" ` +
    `color="#000000" flags="print" name="${uuid}" ` +
    `title="${escapeXml(userName)}" subject="Signature" ` +
    `date="${date}" creationdate="${date}">` +
    `<trn-custom-data bytes="${escapeXml(
      '{"trn-annot-maintain-aspect-ratio":"true"}'
    )}"/>` +
    `<inklist>${gestures}</inklist>` +
    `</ink>` +
    `</annots></xfdf>`
  );
}

export function buildStandaloneFreetextXfdf(
  text: string,
  fontFamily: string,
  userName: string
): string {
  const date = formatPdfDate();
  const uuid = generateUuid();
  const style = `font-family: ${fontFamily};`;

  return (
    `<?xml version="1.0" encoding="UTF-8" ?>` +
    `<xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve">` +
    `<annots>` +
    `<freetext page="0" rect="0.000,0.000,200.000,50.000" ` +
    `color="#000000" flags="print" name="${uuid}" ` +
    `title="${escapeXml(userName)}" subject="Signature" ` +
    `style="${escapeXml(style)}" ` +
    `date="${date}" creationdate="${date}">` +
    `<contents>${escapeXml(text)}</contents>` +
    `<defaultstyle>${escapeXml(style)}</defaultstyle>` +
    `</freetext>` +
    `</annots></xfdf>`
  );
}

export type { StrokePoint };
